import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useDbContext } from "../../../context/DbContext";
import { fetchActiveFinancialYear } from "../../../app/financialYearSlice";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../NavBars/ActionToolbar";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import { FiSave, FiLogOut } from "react-icons/fi";

const borderStyles = `
  /* Outer card */
  .fyc-outer-card        { border: 1px solid #e2e8f0; }

  /* Left panel */
  .fyc-left-panel        { border: 1px solid #e2e8f0; }

  /* Header icon box */
  .fyc-header-icon-box   { border: 1px solid rgba(255, 255, 255, 0.3); }

  /* Inputs & select */
  .fyc-input             { border: 1px solid #cbd5e1; }
  .fyc-input:focus       { border-color: #1a3abf; }

  /* Duration info bar */
  .fyc-info-bar          { border: 1px solid #bfdbfe; }

  /* Warning alert bar */
  .fyc-alert-bar         { border: 1px solid #fef08a; }

  /* Right sidebar cards */
  .fyc-right-card        { border: 1px solid #e2e8f0; }

  /* Table */
  .fyc-table             { border-collapse: collapse; }
  .fyc-thead             { border-bottom: 1px solid #e2e8f0; }
  .fyc-table-row         { border-bottom: 1px solid #f1f5f9; }

  /* Fiscal year list items */
  .fyc-fy-closed         { border: 1px solid #e2e8f0; }
  .fyc-fy-active         { border: 1px solid #213575; }
`;

function FinancialYearChange({ onClose, selectedYear, mode, onSaveSuccess, zIndex }) {
  const dispatch = useDispatch();
  const { dbCode } = useDbContext();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const userRegisterId = sessionStorage.getItem("userRegisterId") || 1;
  const agentCode = sessionStorage.getItem("agentCode") || 1;

  const [fyid, setFyid] = useState(0);
  const [currentFyID, setCurrentFyID] = useState(0);
  const [existingYears, setExistingYears] = useState([]);
  const [voucherNumbering, setVoucherNumbering] = useState([]);
  const [createdUserName, setCreatedUserName] = useState("ADMIN");
  
  const [formData, setFormData] = useState({
    startDate: "2025-04-01",
    endDate: "2028-03-31",
    status: "Active",
    narration: "",
  });

  const [durationText, setDurationText] = useState("");

  // Helper to format date string from DD/MM/YYYY to YYYY-MM-DD
  const parseDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const cleanStr = dateStr.trim();
    const parts = cleanStr.split("/");
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  // Helper to format date string to DD Month YYYY
  const formatDateToDisplay = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Calculate Duration
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const years = (diffDays / 365.25).toFixed(1);
        setDurationText(
          `Duration: ${years} Years (${formatDateToDisplay(formData.startDate)} — ${formatDateToDisplay(formData.endDate)})`
        );
      } else {
        setDurationText("");
      }
    } else {
      setDurationText("");
    }
  }, [formData.startDate, formData.endDate]);

  // Helper to format date object to YYYY-MM-DD
  const toISODateString = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load Initial Details (Next ID, Active Year, Existing Years)
  useEffect(() => {
    const initData = async () => {
      if (!dbCode) return;
      try {
        // Fetch existing years for the right sidebar
        const yearsRes = await axios.get(`${apiBaseUrl}/financialyear/getFinancialYears/${dbCode}`);
        if (yearsRes.data.success) {
          setExistingYears(yearsRes.data.data);
        }

        // Fetch active financial year ID
        const activeRes = await axios.get(`${apiBaseUrl}/financialyear/getActiveFinancialYear/${dbCode}`);
        let activeId = 0;
        let activeData = null;
        if (activeRes.data.success && activeRes.data.data) {
          activeId = activeRes.data.data.Fyid;
          activeData = activeRes.data.data;
          setCurrentFyID(activeId);
        }

        // Fetch salesman name matching the logged-in agentCode
        let currentSalesmanName = "ADMIN";
        try {
          const salesmanRes = await axios.get(`${apiBaseUrl}/main/schsalesmanNames/${dbCode}`);
          if (salesmanRes.data) {
            const matchedSalesman = salesmanRes.data.find(s => s.Auto === parseInt(agentCode));
            if (matchedSalesman) {
              currentSalesmanName = matchedSalesman.Name;
            }
          }
        } catch (salesmanErr) {
          console.error("Error fetching salesman name:", salesmanErr.message);
        }

        if (mode === "edit" && selectedYear) {
          // Edit Mode
          setFyid(selectedYear.ID);
          setCreatedUserName(selectedYear.CreatedUser || "—");
          setFormData({
            startDate: parseDisplayDate(selectedYear.FromDate),
            endDate: parseDisplayDate(selectedYear.ToDate),
            status: selectedYear.Status || "Active",
            narration: selectedYear.Narration === "—" ? "" : selectedYear.Narration || "",
          });
        } else {
          // New Mode - Get Next ID
          setCreatedUserName(currentSalesmanName);
          const nextIdRes = await axios.get(`${apiBaseUrl}/financialyear/getNextFinancialYearId/${dbCode}`);
          if (nextIdRes.data.success) {
            setFyid(nextIdRes.data.data);
          }

          // Dynamic Defaults
          if (activeData && activeData.Frmdate && activeData.Todate) {
            const activeFromDate = new Date(activeData.Frmdate);
            const activeToDate = new Date(activeData.Todate);
            
            // Shift both start and end dates forward by exactly 1 year
            const nextStart = new Date(activeFromDate);
            nextStart.setFullYear(nextStart.getFullYear() + 1);

            const nextEnd = new Date(activeToDate);
            nextEnd.setFullYear(nextEnd.getFullYear() + 1);

            setFormData({
              startDate: toISODateString(nextStart),
              endDate: toISODateString(nextEnd),
              status: "Active",
              narration: "",
            });
          } else {
            // No active year: default to current calendar year's April 1st to next year's March 31st
            const today = new Date();
            const currentYear = today.getFullYear();
            const currentMonth = today.getMonth(); // 0-indexed
            let startYear = currentYear;
            if (currentMonth < 3) { // Jan, Feb, Mar are part of the previous year's start
              startYear = currentYear - 1;
            }
            setFormData({
              startDate: `${startYear}-04-01`,
              endDate: `${startYear + 1}-03-31`,
              status: "Active",
              narration: "",
            });
          }
        }
      } catch (err) {
        console.error("Error loading financial year details:", err.message);
      }
    };
    initData();
  }, [dbCode, selectedYear]);

  // Fetch Voucher Prefix Letter changes when Dates or Current Year ID changes
  useEffect(() => {
    const fetchPrefixes = async () => {
      if (!dbCode || !formData.startDate || !formData.endDate || !currentFyID) return;
      try {
        const response = await axios.post(`${apiBaseUrl}/financialyear/findInvoiceLetters/${dbCode}`, {
          startDate: formData.startDate,
          endDate: formData.endDate,
          currentFyID: currentFyID,
        });

        if (response.data.success) {
          // Map to rows with customized inputs
          const mapped = response.data.data.map((item) => ({
            inl: item.inl,
            voucher: item.voucher,
            newInl: item.NewInl,
            customInl: item.NewInl // initial value is the suggested prefix
          }));
          setVoucherNumbering(mapped);
        }
      } catch (err) {
        console.error("Error finding invoice letters:", err.message);
      }
    };
    fetchPrefixes();
  }, [dbCode, formData.startDate, formData.endDate, currentFyID]);

  // Handle customized prefix change
  const handlePrefixChange = (index, value) => {
    setVoucherNumbering((prev) => {
      const updated = [...prev];
      updated[index].customInl = value;
      return updated;
    });
  };

  // Submit / Save
  const handleSave = async () => {
    if (!formData.startDate || !formData.endDate) {
      alert("Please fill in start and end dates.");
      return;
    }

    try {
      // Map customized prefixes into StringDataTable2 TVP rows
      const typeInvoice = voucherNumbering.map((item) => ({
        MasterString: item.customInl,
        MasterString2: item.voucher,
      }));

      const payload = {
        fyid: mode === "edit" ? fyid : 0,
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: formData.status,
        narration: formData.narration,
        createduser: userRegisterId,
        createddate: new Date().toISOString(),
        currentFyID: currentFyID,
        typeInvoice: typeInvoice,
      };

      const response = await axios.post(`${apiBaseUrl}/financialyear/insertFinancialYear/${dbCode}`, payload);
      if (response.data.success) {
        alert("Financial Year Saved successfully");
        if (dbCode) {
          dispatch(fetchActiveFinancialYear(dbCode));
        }
        if (onSaveSuccess) onSaveSuccess();
      } else {
        alert("Failed to save financial year");
      }
    } catch (err) {
      console.error("Error saving financial year:", err.message);
      alert("Error: " + err.message);
    }
  };

  return (
    <div
      className="absolute inset-0 w-full h-full bg-[#f0f0f0] flex items-center justify-center pointer-events-auto font-inter"
      style={{ zIndex: zIndex || 101 }}
    >
      <style>{borderStyles}</style>

      <div className="fyc-outer-card bg-white font-normal w-[99%] max-w-[1440px] h-[99%] max-h-[900px] shadow-md flex flex-col items-center absolute overflow-hidden">
        {/* Main Content Area */}
        <div className="w-full flex-1 overflow-hidden min-h-0 flex bg-[#f8fafc] p-4 gap-4 box-border">
          {/* Left Side: Form */}
          <div
            className="fyc-left-panel flex-1 flex flex-col overflow-y-auto scrollbar-hide bg-white rounded-lg shadow-sm relative"
          >
            {/* Title Section */}
            <div className="bg-[#213575] text-white rounded-t-lg p-3 px-4 box-border flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div
                  className="fyc-header-icon-box w-9 h-9 rounded-md flex items-center justify-center bg-transparent shrink-0"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
                <div className="flex flex-col">
                  <h2 className="text-[15px] font-semibold m-0 tracking-wide">
                    Financial Year Change
                  </h2>
                  <span className="text-[11px] text-blue-200 mt-0.5 font-light">
                    {selectedYear ? "Modify existing fiscal year entry" : "Create a new fiscal year entry"}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-6 h-6 bg-red-600 hover:bg-red-700 border-none rounded-[1px] mr-2 flex items-center justify-center text-white transition-colors duration-200 focus:outline-none shrink-0"
                aria-label="Close"
              >
                <svg
                  className="w-4 h-4 fill-current"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M18.3 5.71a1 1 0 00-1.41 0L12 10.59 7.11 5.7a1 1 0 10-1.41 1.41L10.59 12l-4.89 4.89a1 1 0 001.41 1.41L12 13.41l4.89 4.89a1 1 0 001.41-1.41L13.41 12l4.89-4.89a1 1 0 000-1.41z"
                  />
                </svg>
              </button>
            </div>

            {/* Form Section */}
            <div className="flex flex-col gap-3 shrink-0 p-4 box-border">
              {/* Fiscal Period */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-[#1e293b]">
                  <div className="w-1.5 h-4 bg-[#213575] rounded-full"></div>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-[#213575]"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <h3 className="text-[14px] font-bold m-0 tracking-wide">
                    Fiscal Period (ID: {fyid})
                  </h3>
                </div>

                <div className="flex gap-3">
                  <div className="flex-1 flex flex-col gap-1">
                    <label className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) =>
                        setFormData({ ...formData, startDate: e.target.value })
                      }
                      className="fyc-input w-full px-2 py-1.5 text-[12px] rounded-md outline-none box-border text-[#334155]"
                    />
                    <span className="text-[10px] text-[#94a3b8]">
                      Beginning of fiscal year
                    </span>
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    <label className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) =>
                        setFormData({ ...formData, endDate: e.target.value })
                      }
                      className="fyc-input w-full px-2 py-1.5 text-[12px] rounded-md outline-none box-border text-[#334155]"
                    />
                    <span className="text-[10px] text-[#94a3b8]">
                      End of fiscal year
                    </span>
                  </div>
                </div>

                {durationText && (
                  <div className="fyc-info-bar bg-[#eff6ff] rounded-md p-2 flex items-center gap-2 mt-0.5">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-[#3b82f6]"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span className="text-[12px] text-[#1d4ed8] font-medium">
                      {durationText}
                    </span>
                  </div>
                )}
              </div>

              {/* Configuration */}
              <div className="flex flex-col gap-2 mt-1">
                <div className="flex items-center gap-2 text-[#1e293b]">
                  <div className="w-1.5 h-4 bg-[#213575] rounded-full"></div>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-[#213575]"
                  >
                    <path d="M12 20V10"></path>
                    <path d="M18 20V4"></path>
                    <path d="M6 20v-4"></path>
                  </svg>
                  <h3 className="text-[14px] font-bold m-0 tracking-wide">
                    Configuration
                  </h3>
                </div>

                <div className="flex gap-3">
                  <div className="w-1/4 flex flex-col gap-1">
                    <label className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value })
                      }
                      className="fyc-input w-full px-2 py-1.5 text-[12px] rounded-md outline-none box-border bg-white text-[#334155]"
                    >
                      <option value="Active">Active</option>
                      <option value="Closed">Closed</option>
                    </select>
                  </div>
                  <div className="w-1/4 flex flex-col gap-1">
                    <label className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider">
                      Created By
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={createdUserName}
                      className="fyc-input w-full px-2 py-1.5 text-[12px] rounded-md outline-none box-border bg-slate-50 text-slate-500 font-medium"
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    <label className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider">
                      Narration
                    </label>
                    <input
                      type="text"
                      placeholder="Add notes..."
                      value={formData.narration}
                      onChange={(e) =>
                        setFormData({ ...formData, narration: e.target.value })
                      }
                      className="fyc-input w-full px-2 py-1.5 text-[12px] rounded-md outline-none box-border text-[#334155]"
                    />
                    <span className="text-[10px] text-[#94a3b8]">
                      Optional description
                    </span>
                  </div>
                </div>
              </div>

              {/* Alert */}
              <div className="fyc-alert-bar bg-[#fefce8] rounded-md p-2 flex items-start gap-2 mt-1">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-[#ca8a04] mt-0.5 shrink-0"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <span className="text-[11px] text-[#854d0e] leading-relaxed">
                  Changing the financial year affects all accounting reports and
                  entries. Ensure a complete data backup before saving changes.
                </span>
              </div>

              {/* Custom Action Toolbar */}
              <div className="mt-2 pt-2">
                <ActionToolbar
                  width="100%"
                  bgColor="transparent"
                  showSave={false}
                  showPrint={false}
                  showClear={false}
                  showFind={false}
                  showEdit={false}
                  showDelete={false}
                  showExit={false}
                  showNavigation={false}
                  className="border-none p-0 !border-t-0 !border-b-0"
                  customButtons={[
                    {
                      text: "Save Changes",
                      icon: <FiSave size={16} />,
                      onClick: handleSave,
                      className:
                        "!bg-[#213575] !text-white hover:!bg-[#1e3a8a] !border-none !flex-row !h-[42px] !py-0 !px-5 !w-auto font-medium text-[13px] cursor-pointer",
                      iconColor: "white",
                      style: { borderRadius: "6px" },
                    },
                    {
                      text: "Clear",
                      icon: (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="rotate-45"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        </svg>
                      ),
                      onClick: () =>
                        setFormData({
                          startDate: "",
                          endDate: "",
                          status: "Active",
                          narration: "",
                        }),
                      className:
                        "!bg-white !text-slate-700 !border-slate-300 hover:!bg-slate-50 !flex-row !h-[42px] !py-0 !px-5 !w-auto font-medium text-[13px] ml-1 cursor-pointer",
                      iconColor: "#334155",
                      style: { borderRadius: "6px" },
                    },
                    {
                      text: "Exit",
                      icon: <FiLogOut size={16} />,
                      onClick: onClose,
                      className:
                        "!bg-white !text-red-500 !border-red-300 hover:!bg-red-50 !flex-row !h-[42px] !py-0 !px-5 !w-auto !ml-auto font-medium text-[13px] cursor-pointer",
                      iconColor: "#ef4444",
                      style: { borderRadius: "6px" },
                    },
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Right Side: Sidebar Tables */}
          <div className="w-[380px] shrink-0 flex flex-col gap-4">
            {/* Voucher Numbering Table */}
            <div className="fyc-right-card bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
              <div className="bg-[#213575] text-white p-3.5 flex items-center gap-2">
                <span className="font-bold">#</span>
                <h3 className="text-[14px] font-semibold m-0 tracking-wide">
                  Voucher Numbering
                </h3>
              </div>
              <div className="p-0 overflow-auto max-h-[220px]">
                <table className="fyc-table w-full text-[12px] text-left">
                  <thead className="fyc-thead bg-slate-50 text-slate-500 sticky top-0">
                    <tr>
                      <th className="py-2 px-3 font-medium">#</th>
                      <th className="py-2 px-3 font-medium">Voucher</th>
                      <th className="py-2 px-3 font-medium text-center">Pr. Letter</th>
                      <th className="py-2 px-3 font-medium text-center">Inv. Letter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voucherNumbering.map((item, index) => (
                      <tr key={index} className="fyc-table-row hover:bg-slate-50">
                        <td className="py-2 px-3 text-slate-500">{index + 1}</td>
                        <td className="py-2 px-3 font-medium text-slate-700">{item.voucher}</td>
                        <td className="py-2 px-3 text-center">
                          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold">
                            {item.inl}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <input
                            type="text"
                            value={item.customInl}
                            onChange={(e) => handlePrefixChange(index, e.target.value)}
                            className="w-16 text-center border border-slate-300 rounded px-1.5 py-0.5 text-[11px] font-bold text-blue-800 focus:outline-none focus:border-[#213575]"
                          />
                        </td>
                      </tr>
                    ))}
                    {voucherNumbering.length === 0 && (
                      <tr>
                        <td colSpan="4" className="py-4 text-center text-slate-400 text-[11px]">
                          Enter dates to calculate prefix settings.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Existing Fiscal Years List */}
            <div className="fyc-right-card bg-white rounded-lg shadow-sm overflow-hidden flex flex-col flex-1">
              <div className="bg-[#213575] text-white p-3.5 flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                  <polyline points="2 12 12 17 22 12"></polyline>
                  <polyline points="2 17 12 22 22 17"></polyline>
                </svg>
                <h3 className="text-[14px] font-semibold m-0 tracking-wide">
                  Existing Fiscal Years
                </h3>
              </div>
              <div className="p-3 overflow-auto flex flex-col gap-3 max-h-[350px]">
                {existingYears.map((year) => {
                  const isActive = year.Status === "Active";
                  return (
                    <div
                      key={year.ID}
                      className={`rounded-md p-3 hover:bg-slate-50 transition-colors flex flex-col gap-2 relative border ${
                        isActive ? "fyc-fy-active bg-[#eef2f6]" : "fyc-fy-closed bg-white"
                      }`}
                    >
                      <div className="absolute right-3 top-3 flex items-center gap-1.5">
                        <div className={`w-1 h-3.5 rounded-full ${isActive ? "bg-green-500" : "bg-red-500"}`}></div>
                        <span className={`text-[11px] font-bold ${isActive ? "text-green-600" : "text-red-600"}`}>
                          {year.Status}
                        </span>
                      </div>
                      <h4 className="text-[13px] font-bold text-slate-800 m-0 pr-16">
                        {year.FromDate} — {year.ToDate}
                      </h4>
                      <div className="flex gap-2 text-[11px] text-slate-500 mt-1">
                        <span>By {year.CreatedUser || "—"}</span>
                        <span>•</span>
                        <span>{year.CreatedDate || "—"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FinancialYearChange;
